import axios from "axios";
import { getAuthHeader } from "../utils/authHeader";
import { AdminBrokerage } from "../types/brokerages";

interface SignedUrlResponse {
  signedUrl: string;
  key: string;
}

export interface LogoUploadMeta {
  contentType: string;
  extension: string;
  fileSize: number;
}

class BrokerageService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_BACKEND_URL ?? "";
  }

  async getBrokerages(): Promise<AdminBrokerage[]> {
    const headers = await getAuthHeader();
    const response = await axios.get<AdminBrokerage[]>(
      `${this.baseUrl}/admin/masters/brokerages`,
      { headers },
    );
    return response.data;
  }

  async updateColors(
    id: string,
    zapcapColors: string[],
  ): Promise<AdminBrokerage> {
    const headers = await getAuthHeader();
    const response = await axios.patch<AdminBrokerage>(
      `${this.baseUrl}/admin/masters/brokerages/${id}/colors`,
      { zapcapColors },
      { headers },
    );
    return response.data;
  }

  async getLogoUploadUrl(
    id: string,
    meta: LogoUploadMeta,
  ): Promise<SignedUrlResponse> {
    const headers = await getAuthHeader();
    const response = await axios.post<SignedUrlResponse>(
      `${this.baseUrl}/admin/masters/brokerages/${id}/logos/signed-url`,
      meta,
      { headers },
    );
    return response.data;
  }

  // Step 2: PUT the cropped blob straight to GCS. Raw request, no auth header —
  // the signed URL is self-authorizing and extra headers break the signature.
  async uploadToSignedUrl(
    signedUrl: string,
    blob: Blob,
    contentType: string,
  ): Promise<void> {
    await axios.put(signedUrl, blob, {
      headers: { "Content-Type": contentType },
    });
  }

  async confirmLogo(id: string, key: string): Promise<AdminBrokerage> {
    const headers = await getAuthHeader();
    const response = await axios.post<AdminBrokerage>(
      `${this.baseUrl}/admin/masters/brokerages/${id}/logos`,
      { key },
      { headers },
    );
    return response.data;
  }

  async removeLogo(id: string, logoId: string): Promise<AdminBrokerage> {
    const headers = await getAuthHeader();
    const response = await axios.delete<AdminBrokerage>(
      `${this.baseUrl}/admin/masters/brokerages/${id}/logos/${logoId}`,
      { headers },
    );
    return response.data;
  }

  async createBrokerage(name: string): Promise<AdminBrokerage> {
    const headers = await getAuthHeader();
    const response = await axios.post<AdminBrokerage>(
      `${this.baseUrl}/admin/masters/brokerages`,
      { name },
      { headers },
    );
    return response.data;
  }

  async updateBrokerageName(id: string, name: string): Promise<AdminBrokerage> {
    const headers = await getAuthHeader();
    const response = await axios.patch<AdminBrokerage>(
      `${this.baseUrl}/admin/masters/brokerages/${id}/name`,
      { name },
      { headers },
    );
    return response.data;
  }

  async deleteBrokerage(id: string): Promise<void> {
    const headers = await getAuthHeader();
    await axios.delete(`${this.baseUrl}/admin/masters/brokerages/${id}`, {
      headers,
    });
  }

  async toggleStatus(id: string, isEnabled: boolean): Promise<AdminBrokerage> {
    const headers = await getAuthHeader();
    const response = await axios.patch<AdminBrokerage>(
      `${this.baseUrl}/admin/masters/brokerages/${id}/status`,
      { isEnabled },
      { headers },
    );
    return response.data;
  }
}

export const brokerageService = new BrokerageService();
